import { useState, useEffect, useRef, useCallback, useMemo } from "react";

const navItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Team", href: "/team" },
  { label: "FAQ", href: "/faq" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
  { label: "Support", href: "/support" },
  { label: "Tech Stack", href: "/tech-stack" },
  { label: "Changelog", href: "/changelog" },
];

const HighlightedText = ({ text, query }) => {
  if (!query.trim()) return <span>{text}</span>;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            className="bg-[#FF7C53]/20 text-[#FB5B38] rounded px-0.5"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return navItems;
    const lower = query.toLowerCase();
    return navItems.filter((item) =>
      item.label.toLowerCase().includes(lower)
    );
  }, [query]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filtered]);

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      // Small delay to ensure the DOM is ready
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const navigate = useCallback(
    (href) => {
      close();
      window.location.href = href;
    },
    [close]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filtered.length);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (filtered[selectedIndex]) {
          navigate(filtered[selectedIndex].href);
        }
      }
    },
    [close, filtered, selectedIndex, navigate]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.children[selectedIndex];
      if (selected) {
        selected.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-label="Command palette"
        className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl overflow-hidden font-outfit"
        style={{ animation: "cmdFadeIn 0.15s ease-out" }}
      >
        {/* Search input */}
        <div className="flex items-center border-b border-gray-100 px-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B8B8B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full py-4 px-3 text-base text-[#1B1B1B] placeholder-[#8B8B8B] bg-transparent outline-none font-outfit"
            aria-label="Search navigation"
            autoComplete="off"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs text-[#8B8B8B] bg-gray-100 rounded font-mono shrink-0">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <ul
          ref={listRef}
          className="max-h-[320px] overflow-y-auto py-2"
          role="listbox"
          aria-label="Navigation results"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-[#8B8B8B] text-sm">
              No results found for &ldquo;{query}&rdquo;
            </li>
          ) : (
            filtered.map((item, index) => (
              <li
                key={item.href}
                role="option"
                aria-selected={index === selectedIndex}
                onClick={() => navigate(item.href)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-100 ${
                  index === selectedIndex
                    ? "bg-[#FF7C53]/10 text-[#1B1B1B]"
                    : "text-[#1B1B1B] hover:bg-gray-50"
                }`}
              >
                {/* Page icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={index === selectedIndex ? "#FF7C53" : "#8B8B8B"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="shrink-0"
                >
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                  <polyline points="13 2 13 9 20 9" />
                </svg>

                <span className="text-sm font-medium">
                  <HighlightedText text={item.label} query={query} />
                </span>

                {index === selectedIndex && (
                  <span className="ml-auto text-xs text-[#8B8B8B]">Enter to open</span>
                )}
              </li>
            ))
          )}
        </ul>

        {/* Footer hint */}
        <div className="border-t border-gray-100 px-4 py-2.5 flex items-center gap-4 text-xs text-[#8B8B8B]">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">&uarr;</kbd>
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">&darr;</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">Enter</kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">Esc</kbd>
            close
          </span>
        </div>
      </div>

      {/* Inline animation */}
      <style>{`
        @keyframes cmdFadeIn {
          from {
            opacity: 0;
            transform: scale(0.96) translateY(-8px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CommandPalette;
